import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { AssistantCron } from '../../types/assistantCron';
import { ALLOWED_INTERVALS, INTERVAL_LABELS } from '../../types/assistantCron';
import type { Assistant } from '../../types/assistant';
import { formatDateTime } from '../../lib/formatters';
import { Form, FormField, FormLabel } from '../ui';
import { FormInput, FormSelect, FormTextarea } from '../ui/FormControls';
import { FormCheckbox } from '../ui/FormCheckbox';
import Button from '../ui/Button';
import Badge from '../ui/Badge';

const cronSchema = z.object({
  assistant_id: z.string().min(1, 'Assistente é obrigatório'),
  name: z.string().min(1, 'Nome é obrigatório').max(100, 'Nome deve ter no máximo 100 caracteres'),
  prompt: z.string().min(1, 'Prompt é obrigatório'),
  interval_minutes: z.string().min(1, 'Intervalo é obrigatório'),
  enabled: z.boolean().optional(),
  filter_min_tickets_pending: z.string().optional().refine(
    val => !val || (!isNaN(Number(val)) && Number(val) >= 0),
    'Deve ser um número inteiro maior ou igual a zero'
  ),
});

type FormData = z.infer<typeof cronSchema>;

interface AssistantCronFormProps {
  cron: AssistantCron | null;
  assistants: Assistant[];
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
  onClose: () => void;
}

function AssistantCronForm({
  cron,
  assistants,
  onSubmit,
  isLoading,
  onClose,
}: AssistantCronFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(cronSchema),
    defaultValues: {
      assistant_id: cron?.assistant_id?.toString() || '',
      name: cron?.name || '',
      prompt: cron?.prompt || '',
      interval_minutes: cron?.interval_minutes?.toString() || '',
      enabled: cron?.enabled ?? true,
      filter_min_tickets_pending: cron?.filter_min_tickets_pending?.toString() || '',
    },
  });

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-4 pt-4">
        <FormField>
          <FormLabel htmlFor="assistant_id" required>
            Assistente
          </FormLabel>
          <FormSelect
            {...register('assistant_id')}
            error={!!errors.assistant_id}
            validationError={errors.assistant_id?.message}
          >
            <option value="">Selecione um assistente</option>
            {assistants.map(assistant => (
              <option key={assistant.id} value={assistant.id}>
                {assistant.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="name" required>
            Nome
          </FormLabel>
          <FormInput
            {...register('name')}
            error={!!errors.name}
            validationError={errors.name?.message}
            maxLength={100}
            placeholder="Nome identificador do cron"
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="prompt" required>
            Prompt
          </FormLabel>
          <FormTextarea
            {...register('prompt')}
            error={!!errors.prompt}
            validationError={errors.prompt?.message}
            rows={8}
            placeholder="Prompt executado pelo cron..."
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="interval_minutes" required>
            Intervalo de Execução
          </FormLabel>
          <FormSelect
            {...register('interval_minutes')}
            error={!!errors.interval_minutes}
            validationError={errors.interval_minutes?.message}
          >
            <option value="">Selecione um intervalo</option>
            {ALLOWED_INTERVALS.map(value => (
              <option key={value} value={value}>
                {INTERVAL_LABELS[value]}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="filter_min_tickets_pending">
            Mínimo de Tickets Pendentes
          </FormLabel>
          <FormInput
            {...register('filter_min_tickets_pending')}
            type="number"
            min="0"
            placeholder="Opcional: só executa se houver pelo menos esse número de tickets pendentes"
            error={!!errors.filter_min_tickets_pending}
            validationError={errors.filter_min_tickets_pending?.message}
          />
          <p className="mt-2 text-sm text-gray-500">
            Define um filtro que só permite a execução do cron quando há pelo menos essa quantidade de tickets pendentes.
          </p>
        </FormField>

        <FormField>
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <FormCheckbox {...register('enabled')} id="enabled" />
            </div>
            <div className="ml-3 text-sm">
              <FormLabel htmlFor="enabled" className="font-medium">
                Ativo
              </FormLabel>
              <p className="text-gray-500">Define se o cron está ativo e será executado</p>
            </div>
          </div>
        </FormField>
      </div>

      {cron && (
        <div className="mt-6 space-y-4 rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Informações de Execução
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Status da última execução</span>
              <div className="mt-1">
                {cron.last_status ? (
                  <Badge
                    variant={
                      cron.last_status.toLowerCase() === 'success' || cron.last_status.toLowerCase() === 'succeeded'
                        ? 'success'
                        : cron.last_status.toLowerCase() === 'failed' || cron.last_status.toLowerCase() === 'error'
                        ? 'error'
                        : cron.last_status.toLowerCase() === 'running'
                        ? 'info'
                        : 'warning'
                    }
                  >
                    {cron.last_status}
                  </Badge>
                ) : (
                  <span className="text-sm text-gray-500 dark:text-gray-400">Nunca executado</span>
                )}
              </div>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Última execução</span>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {cron.last_run_at ? formatDateTime(cron.last_run_at) : '-'}
              </p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Próxima execução</span>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {cron.next_run_at ? formatDateTime(cron.next_run_at) : '-'}
              </p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Criado em</span>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {cron.created_at ? formatDateTime(cron.created_at) : '-'}
              </p>
            </div>
          </div>
          {cron.last_message && (
            <div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Mensagem da última execução</span>
              <p className="mt-1 whitespace-pre-wrap break-words rounded-md bg-white p-2 text-sm text-gray-900 dark:bg-gray-700 dark:text-gray-100">
                {cron.last_message}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {cron ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default AssistantCronForm;
